import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { has, cloneDeep } from 'lodash';

import { AuthService } from '../auth.service';
import { AUTH_PATHS, AuthDataInterface, ADFSSettings } from '../auth';
import { MAIN_PATHS } from '../../main/main';
import { USER_PATHS } from '../../user/user';
import { Enums } from '../../../configs/Enums';
import { SocketService } from '../../../services/socket.service';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-login-modal-change-password',
  templateUrl: './tmpl/login.modal.change-password.html',
  standalone: false,
})
export class LoginModalChangePasswordComponent {
  public Enums = Enums;

  @Input() title;
  @Input() content;
  @Input() isHasExpiriesPassword;

  constructor(public activeModal: NgbActiveModal) {}
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  public Enums = Enums;
  public AUTH_PATHS = AUTH_PATHS;
  public authError: string | null;
  public authErrorLogin: boolean | null;
  public authData: AuthDataInterface;
  public authLoading: boolean;
  public authForm: UntypedFormGroup = new UntypedFormGroup({
    login: new UntypedFormControl(''),
    password: new UntypedFormControl(''),
  });
  public ADFSSettings: ADFSSettings;
  public isUseADFS: boolean;
  public ADFSLoading: boolean;

  constructor(
    private authService: AuthService,
    private modalService: NgbModal,
    private router: Router,
    private route: ActivatedRoute,
    private socketService: SocketService,
    private breadcrumbsService: BreadcrumbsService
  ) {}

  ngOnInit() {
    this.breadcrumbsService.updateBreadcrumbs([]);
    this.checkUseADFS();
  }

  private checkUseADFS() {
    this.ADFSLoading = true;
    this.authService.checkUseADFS().subscribe(
      (response) => {
        this.ADFSSettings = response.data;
        this.isUseADFS = this.ADFSSettings.adfs_auth_enable;

        if (this.isUseADFS) {
          this.authForm.controls['login'].setValue(this.authService.getADFSData().login || '');
        }

        this.ADFSLoading = false;
      },
      (error) => {
        this.isUseADFS = false;
        this.ADFSLoading = false;
      }
    );
  }

  public onSubmitAuthForm(): void {
    const authLoginFormObj = cloneDeep(this.authForm.value);

    if (this.isUseADFS) {
      authLoginFormObj.login = this.ADFSSettings.adfs_domain + '\\' + authLoginFormObj.login;
    }

    this.authError = null;
    this.authErrorLogin = null;

    this.authLoading = true;
    this.authService.login(authLoginFormObj).subscribe(
      (response) => {
        this.checkResponse(response['data'], response['metadata']);
        this.socketService.connect();

        if (this.isUseADFS) {
          this.authService.setADFSData({
            login: this.authForm.value.login,
          });
        }

        this.authLoading = false;
      },
      (error) => {
        this.checkResponse(null, error.error['metadata']);
        this.authLoading = false;
      }
    );
  }

  private checkResponse(authData: AuthDataInterface, respMetadata: any): void {
    this.authData = authData;

    if (has(respMetadata, 'code')) {
      this.checkCode(authData, respMetadata);
    } else {
      this.authError = Enums.errorCodes[Enums.errorCodesMaps['BACKEND_ERROR']];
    }
  }

  private checkCode(authData: AuthDataInterface, respMetadata): void {
    switch (respMetadata.code) {
      case 0:
        this.finalAuth();
        return;
      case 100000:
        this.authError = respMetadata.message || Enums.errorCodes[Enums.errorCodesMaps['BACKEND_ERROR']];
        return;
      case 100001:
        this.authError = respMetadata.message;
        this.authErrorLogin = true;
        return;
      case 100002:
        this.openExpiriesPasswordModal(true, authData);
        return;
      case 100003:
        this.openExpiriesPasswordModal(false, authData);
        return;
      default:
        if (respMetadata.code) {
          this.authError = respMetadata.message;
        }

        return;
    }
  }

  private finalAuth(): void {
    let url = this.route.snapshot.queryParamMap.get('url');

    if (url) {
      window.location.href = window.location.origin + '/#' + url;
    } else {
      this.router.navigate([MAIN_PATHS.main]);
    }
  }

  public openExpiriesPasswordModal(isHasExpiriesPassword: boolean, authData: AuthDataInterface): void {
    const modalRef = this.modalService.open(LoginModalChangePasswordComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper modal-wrapper--w-520',
      backdrop: 'static',
      keyboard: false,
    });

    if (isHasExpiriesPassword) {
      modalRef.componentInstance.title = 'Истек срок действия пароля';
      modalRef.componentInstance.content = 'Смените пароль для обеспечения безопасности вашего аккаунта.';
      modalRef.componentInstance.isHasExpiriesPassword = true;
    } else {
      modalRef.componentInstance.title = 'Истекает срок действия пароля';
      modalRef.componentInstance.content = 'Для обеспечения безопасности вашего аккаунта смените пароль в' + ' течение ' + authData.days_left + ' дн.';
      modalRef.componentInstance.isHasExpiriesPassword = false;
    }

    modalRef.result.then(
      () => {
        this.router.navigate([MAIN_PATHS.main]);
      },
      () => {
        if (this.isUseADFS) {
          this.authLoading = true;
          this.authService.logout().subscribe(
            (response) => {
              window.location.href = this.ADFSSettings.adfs_pwd_reset_url;
              this.authLoading = true;
            },
            (error) => {
              this.authLoading = true;
            }
          );
        } else {
          this.router.navigate([USER_PATHS.change_password_auth_user]);
        }
      }
    );
  }
}
